// BETA-CLEANUP: delete this file and its spec once Vue 3 is the only app (store.js goes back to plain cookie options)
//
// One file for both apps, kept identical on develop (the current Vue 2 app) and the Vue 3 branch (the beta),
// so pulling develop into the Vue 3 branch never needs a merge here. What differs is the env:
// - the current app: VITE_IS_BETA empty, VITE_BETA_APP_URL = the beta, VITE_CURRENT_APP_URL empty
// - the beta:        VITE_IS_BETA=true, VITE_CURRENT_APP_URL = the current app, VITE_BETA_APP_URL empty
// - both:            VITE_COOKIE_DOMAIN = the parent domain they share (.slickcactus.com), empty locally
import Cookies from 'js-cookie';

import { IUserReturn } from '@/bundles/Auth/interfaces/IUserReturn';

// This build is the opt-in beta (Vue 3) running next to the current app
export const isBetaApp = import.meta.env.VITE_IS_BETA === 'true';

// The beta the current app offers, e.g. https://beta.app.slickcactus.com
export const betaAppUrl = import.meta.env.VITE_BETA_APP_URL || '';

// The current app the beta links back to, e.g. https://app.slickcactus.com
export const currentAppUrl = import.meta.env.VITE_CURRENT_APP_URL || '';

// The app on the other side of a switch: the one sign-ins are handed to and accepted from
const otherAppUrl = isBetaApp ? currentAppUrl : betaAppUrl;

// "Try the new version" (sidebar link, profile menu promo, intro dialog) shows only on the current app,
// and only once the beta has a URL
export const isBetaAvailable = !isBetaApp && !!betaAppUrl;

// `.slickcactus.com` on both deploys: the parent domain both apps read the sign-in handoff from; empty
// turns the handoff off (local development, staging)
export const sharedCookieDomain = import.meta.env.VITE_COOKIE_DOMAIN || undefined;

// The day the new version becomes the default: November 20, 2026, as a local calendar day
// (`new Date('2026-11-20')` would be UTC midnight, which is still the 19th in the US)
const BETA_RELEASE_DATE = new Date(2026, 10, 20);

export const getBetaReleaseNote = (now: Date = new Date()): string => {
  if (!isBetaAvailable) return '';

  if (now >= BETA_RELEASE_DATE) return 'Now the default version';

  const formatted = BETA_RELEASE_DATE.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return `Becomes the default on ${formatted}`;
};

// vuex-persistedstate's default key, the cookie that holds the sign-in. It stays host-only on each app.
export const SIGN_IN_COOKIE = 'vuex';
export const SIGN_IN_COOKIE_EXPIRES_DAYS = 5;
export const signInCookieAttributes: Cookies.CookieAttributes = Object.freeze({
  secure: true,
  path: '/',
  sameSite: 'strict',
});

const SESSION_HANDOFF_COOKIE = 'sc-session-handoff';
// a minute: it only has to outlive the page load on the other app
const SESSION_HANDOFF_EXPIRES_DAYS = 1 / (24 * 60);

interface ISessionHandoff {
  to: string;
  signIn: string;
}

type PageLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;

// `?stay` on any page of the current app keeps the user there and turns the choice off (e.g. while the beta is
// down); the beta adds it when it could not save "switch back"
const STAY_QUERY = 'stay';

// Both apps share their routes, so the same path and query open the same page on the other one
export const getSamePageUrl = (appUrl: string, { pathname, search, hash }: PageLocation, stay = false): string => {
  const url = new URL(`${pathname}${search}${hash}`, appUrl);

  if (stay) url.searchParams.set(STAY_QUERY, '');

  return url.href;
};

const removeSessionHandoff = (): void => {
  Cookies.remove(SESSION_HANDOFF_COOKIE, { ...signInCookieAttributes, domain: sharedCookieDomain });
};

// Hands the sign-in to the other app for its next page load. Only this short-lived cookie is ever readable
// across the parent domain; the sign-in itself never leaves this host.
export const openOtherApp = (appUrl: string, stay = false): void => {
  const url = getSamePageUrl(appUrl, window.location, stay);
  const signIn = Cookies.get(SIGN_IN_COOKIE);

  if (sharedCookieDomain && signIn !== undefined) {
    const handoff: ISessionHandoff = { to: new URL(url).origin, signIn };

    Cookies.set(SESSION_HANDOFF_COOKIE, JSON.stringify(handoff), {
      ...signInCookieAttributes,
      domain: sharedCookieDomain,
      expires: SESSION_HANDOFF_EXPIRES_DAYS,
    });
  }

  window.location.assign(url);
};

// Imported on use: the store imports this module, and the http layer imports the store
const getAuthService = async () => (await import('@/bundles/Auth/factory/authServiceFactory')).authService;

// The new-version intro dialog stays away while this cookie lives: a week after "Not now", a year once the
// user has tried the beta. A cookie, not localStorage, because sign-out clears localStorage.
const BETA_INTRO_DISMISSED_COOKIE = 'sc-beta-intro-dismissed';
const BETA_INTRO_SNOOZE_DAYS = 7;
const BETA_INTRO_TRIED_DAYS = 365;

export const isBetaIntroDue = (): boolean => (
  isBetaAvailable && Cookies.get(BETA_INTRO_DISMISSED_COOKIE) === undefined
);

interface IBetaIntroContext {
  isDue: boolean;
  isSignedIn: boolean;
  isOtpDialogOpen: boolean;
  isTemporaryPasswordDialogOpen: boolean;
}

// The intro waits for the security dialogs and never opens on top of them
export const shouldShowBetaIntro = ({
  isDue,
  isSignedIn,
  isOtpDialogOpen,
  isTemporaryPasswordDialogOpen,
}: IBetaIntroContext): boolean => isDue && isSignedIn && !isOtpDialogOpen && !isTemporaryPasswordDialogOpen;

export const snoozeBetaIntro = (): void => {
  Cookies.set(BETA_INTRO_DISMISSED_COOKIE, 'true', { ...signInCookieAttributes, expires: BETA_INTRO_SNOOZE_DAYS });
};

// Current app: "Try the new version". The choice lives in the user record (`use_new_app`), so it follows the
// user to any browser. A failed save loses only the "remember" part: the user asked to try the beta, so it
// opens anyway.
export const openBeta = async (): Promise<void> => {
  if (!isBetaAvailable) return;

  Cookies.set(BETA_INTRO_DISMISSED_COOKIE, 'true', { ...signInCookieAttributes, expires: BETA_INTRO_TRIED_DAYS });

  try {
    await (await getAuthService()).setUseNewApp(true);
  } catch {
    // the beta opens either way
  }

  openOtherApp(betaAppUrl);
};

// Beta: "Switch back to the old version". If saving the choice fails, the current app is opened with `?stay`
// and retries the save itself, so the user is not bounced back here.
export const leaveBeta = async (): Promise<void> => {
  if (!isBetaApp || !currentAppUrl) return;

  let stay = false;

  try {
    await (await getAuthService()).setUseNewApp(false);
  } catch {
    stay = true;
  }

  openOtherApp(currentAppUrl, stay);
};

// Current app: runs each time the current user is fetched (boot and fresh sign-in); true when the page is
// leaving for the beta. The beta never redirects anywhere.
export const redirectToPreferredBeta = (user: Pick<IUserReturn, 'use_new_app'>): boolean => {
  if (isBetaApp) return false;

  if (new URLSearchParams(window.location.search).has(STAY_QUERY)) {
    if (user.use_new_app) {
      getAuthService()
        .then(authService => authService.setUseNewApp(false))
        .catch(() => {
          // the next load with `?stay` retries
        });
    }
    return false;
  }

  if (!isBetaAvailable || user.use_new_app !== true) return false;

  openOtherApp(betaAppUrl);
  return true;
};

// Only a page opened from the other app may hand a sign-in over: any host under the parent domain can write
// the cookie, but it cannot fake the referrer the browser sends on that navigation.
const isOpenedFromOtherApp = (): boolean => (
  !!otherAppUrl && !!document.referrer && new URL(document.referrer).origin === new URL(otherAppUrl).origin
);

// The first beta build (Nov 4, 2026) kept the sign-in on the parent domain. That copy outlives every sign-out
// (they clear the host-only one) and, being older, is the one `Cookies.get` returns, so a long-expired token
// would keep winning over each fresh sign-in. Only the parent-domain copy goes; the host-only one stays.
const removeSharedSignIn = (): void => {
  Cookies.remove(SIGN_IN_COOKIE, { ...signInCookieAttributes, domain: sharedCookieDomain });
};

// Runs before the persisted state reads the sign-in; true when it signed the user in. A handoff addressed to
// another host (e.g. staging under the same parent domain) is left for that host.
export const receiveSessionHandoff = (): boolean => {
  if (!sharedCookieDomain) return false;

  removeSharedSignIn();

  const handoffCookie = Cookies.get(SESSION_HANDOFF_COOKIE);

  if (handoffCookie === undefined) return false;

  let handoff: ISessionHandoff;

  try {
    handoff = JSON.parse(handoffCookie);
  } catch {
    removeSessionHandoff();
    return false;
  }

  // `?.`: valid JSON is not always an object (`null`, a number), and a throw here would stop the app from starting
  if (handoff?.to !== window.location.origin) return false;

  removeSessionHandoff();

  if (typeof handoff.signIn !== 'string' || !isOpenedFromOtherApp()) return false;

  Cookies.set(SIGN_IN_COOKIE, handoff.signIn, { ...signInCookieAttributes, expires: SIGN_IN_COOKIE_EXPIRES_DAYS });
  return true;
};
